import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/apiClient';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Truck } from 'lucide-react';
import { toast } from 'sonner';
import type { BillingCustomer, TaxRate, AccountingAccount } from '@/types/billing';

export interface Supplier extends BillingCustomer {
  entity_type: 'supplier' | 'both';
  default_expense_account_id?: string;
}

const emptySupplier: Partial<Supplier> = {
  customer_type: 'company',
  tax_id_type: 'NIF',
  legal_name: '',
  country_code: 'ES',
  default_payment_terms_days: 30,
  credit_limit: 0,
  is_active: true,
  entity_type: 'supplier',
};

interface SupplierFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  supplier?: Partial<Supplier> | null;
  onSuccess?: (supplier: Supplier) => void;
}

export function SupplierFormDialog({
  open,
  onOpenChange,
  supplier,
  onSuccess,
}: SupplierFormDialogProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [editingSupplier, setEditingSupplier] = useState<Partial<Supplier>>(emptySupplier);

  useEffect(() => {
    if (open) {
      setEditingSupplier(supplier ? { ...supplier } : { ...emptySupplier });
    }
  }, [open, supplier]);

  const { data: taxRates = [] } = useQuery<TaxRate[]>({
    queryKey: ['tax-rates'],
    queryFn: async () => {
      const { data, error } = await api
        .from('tax_rates')
        .select('*')
        .eq('is_active', true)
        .order('rate', { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: open,
  });

  const { data: expenseAccounts = [] } = useQuery<AccountingAccount[]>({
    queryKey: ['expense-accounts'],
    queryFn: async () => {
      const { data, error } = await api
        .from('accounting_accounts')
        .select('*')
        .like('code', '6%')
        .eq('is_active', true)
        .eq('is_header', false)
        .order('code');
      if (error) throw error;
      return data || [];
    },
    enabled: open,
  });

  const saveMutation = useMutation({
    mutationFn: async (supplierData: Partial<Supplier>) => {
      const payload = {
        ...supplierData,
        entity_type: supplierData.entity_type || 'supplier',
      };

      if (supplierData.id) {
        const { data, error } = await api
          .from('billing_customers')
          .update({
            ...payload,
            updated_at: new Date().toISOString(),
            updated_by: user?.id,
          })
          .eq('id', supplierData.id)
          .select()
          .single();
        if (error) throw error;
        return data as Supplier;
      } else {
        const { data, error } = await api
          .from('billing_customers')
          .insert({
            ...payload,
            created_by: user?.id,
          })
          .select()
          .single();
        if (error) throw error;
        return data as Supplier;
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      queryClient.invalidateQueries({ queryKey: ['suppliers-selector'] });
      onOpenChange(false);
      toast.success(supplier?.id ? 'Proveedor actualizado' : 'Proveedor creado');
      onSuccess?.(data);
    },
    onError: (error: any) => {
      toast.error(`Error: ${error.message}`);
    },
  });

  const handleSave = () => {
    if (!editingSupplier.legal_name) {
      toast.error('El nombre es obligatorio');
      return;
    }
    saveMutation.mutate(editingSupplier);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="w-5 h-5 text-orange-600" />
            {editingSupplier.id ? 'Editar Proveedor' : 'Nuevo Proveedor'}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="general" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="contact">Contacto</TabsTrigger>
            <TabsTrigger value="accounting">Contabilidad</TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select
                  value={editingSupplier.customer_type}
                  onValueChange={(v) => setEditingSupplier({ ...editingSupplier, customer_type: v as any })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="company">Empresa</SelectItem>
                    <SelectItem value="individual">Autonomo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Relacion</Label>
                <Select
                  value={editingSupplier.entity_type || 'supplier'}
                  onValueChange={(v) => setEditingSupplier({ ...editingSupplier, entity_type: v as any })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="supplier">Solo Proveedor</SelectItem>
                    <SelectItem value="both">Cliente y Proveedor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Tipo documento</Label>
                <Select
                  value={editingSupplier.tax_id_type}
                  onValueChange={(v) => setEditingSupplier({ ...editingSupplier, tax_id_type: v as any })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NIF">NIF</SelectItem>
                    <SelectItem value="CIF">CIF</SelectItem>
                    <SelectItem value="NIE">NIE</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 space-y-2">
                <Label>NIF/CIF *</Label>
                <Input
                  value={editingSupplier.tax_id || ''}
                  onChange={(e) => setEditingSupplier({ ...editingSupplier, tax_id: e.target.value.toUpperCase() })}
                  placeholder="B12345678"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Razon Social *</Label>
              <Input
                value={editingSupplier.legal_name || ''}
                onChange={(e) => setEditingSupplier({ ...editingSupplier, legal_name: e.target.value })}
                placeholder="Nombre legal"
              />
            </div>

            <div className="space-y-2">
              <Label>Nombre Comercial</Label>
              <Input
                value={editingSupplier.trade_name || ''}
                onChange={(e) => setEditingSupplier({ ...editingSupplier, trade_name: e.target.value })}
              />
            </div>
          </TabsContent>

          <TabsContent value="contact" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Persona de contacto</Label>
              <Input
                value={editingSupplier.contact_name || ''}
                onChange={(e) => setEditingSupplier({ ...editingSupplier, contact_name: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={editingSupplier.email || ''}
                  onChange={(e) => setEditingSupplier({ ...editingSupplier, email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Telefono</Label>
                <Input
                  value={editingSupplier.phone || ''}
                  onChange={(e) => setEditingSupplier({ ...editingSupplier, phone: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Direccion</Label>
              <Input
                value={editingSupplier.address_line1 || ''}
                onChange={(e) => setEditingSupplier({ ...editingSupplier, address_line1: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Codigo Postal</Label>
                <Input
                  value={editingSupplier.postal_code || ''}
                  onChange={(e) => setEditingSupplier({ ...editingSupplier, postal_code: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Ciudad</Label>
                <Input
                  value={editingSupplier.city || ''}
                  onChange={(e) => setEditingSupplier({ ...editingSupplier, city: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Provincia</Label>
                <Input
                  value={editingSupplier.province || ''}
                  onChange={(e) => setEditingSupplier({ ...editingSupplier, province: e.target.value })}
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="accounting" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Cuenta de gasto por defecto</Label>
              <Select
                value={editingSupplier.default_expense_account_id || '__none__'}
                onValueChange={(v) => setEditingSupplier({ ...editingSupplier, default_expense_account_id: v === '__none__' ? undefined : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar cuenta..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin cuenta asignada</SelectItem>
                  {expenseAccounts.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      {account.code} - {account.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Cuenta contable del grupo 6 que se usara por defecto al registrar gastos de este proveedor
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Dias de pago</Label>
                <Input
                  type="number"
                  value={editingSupplier.default_payment_terms_days || 30}
                  onChange={(e) => setEditingSupplier({ ...editingSupplier, default_payment_terms_days: parseInt(e.target.value) || 30 })}
                />
              </div>
              <div className="space-y-2">
                <Label>IVA por defecto</Label>
                <Select
                  value={editingSupplier.default_tax_rate_id || '__none__'}
                  onValueChange={(v) => setEditingSupplier({ ...editingSupplier, default_tax_rate_id: v === '__none__' ? undefined : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="IVA general" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">IVA general</SelectItem>
                    {taxRates.map((rate) => (
                      <SelectItem key={rate.id} value={rate.id}>
                        {rate.name} ({rate.rate}%)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>IBAN (para pagos)</Label>
              <Input
                value={editingSupplier.bank_account_iban || ''}
                onChange={(e) => setEditingSupplier({ ...editingSupplier, bank_account_iban: e.target.value.toUpperCase().replace(/\s/g, '') })}
                placeholder="ES00 0000 0000 0000 0000 0000"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Limite de credito</Label>
                <Input
                  type="number"
                  value={editingSupplier.credit_limit || 0}
                  onChange={(e) => setEditingSupplier({ ...editingSupplier, credit_limit: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2 flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingSupplier.is_active !== false}
                    onChange={(e) => setEditingSupplier({ ...editingSupplier, is_active: e.target.checked })}
                    className="rounded"
                  />
                  <span>Proveedor activo</span>
                </label>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notas internas</Label>
              <Textarea
                value={editingSupplier.notes || ''}
                onChange={(e) => setEditingSupplier({ ...editingSupplier, notes: e.target.value })}
                rows={3}
              />
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
